import { useState, useEffect, useMemo } from 'react';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Fade, TextField, InputAdornment, Button, Paper } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '../../common/Pagination';
import LogoLoader from '../../common/LogoLoader';
import * as XLSX from 'xlsx-js-style';

const TMP_TYPE = 1; // Cash

function CashBookReport() {
  useSessionValidation();

  const { companyId, branchId, token, companyName } = useAuth();

  const today = new Date().toISOString().split('T')[0];
  const nowD = new Date();
  const firstOfMonth = `${nowD.getFullYear()}-${String(nowD.getMonth() + 1).padStart(2, '0')}-01`;

  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate] = useState(today);
  const [tmpType] = useState(TMP_TYPE);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => { setMounted(true); }, []);

  const fetchCashBook = async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      // GET /{tmpType}/{companyid}/{branchid}/{fromDate}/{toDate}
      const response = await fetchData(
        'ReportApi',
        `/CashBook/${companyId}/${branchId || 0}/${fromDate}/${toDate}`,
        token
      );
      const rows = response?.response_data || response || [];
      setData(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error('Error fetching cash book:', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => { setCurrentPage(1); fetchCashBook(); };

  const fmt = (n) =>
    parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const filteredData = useMemo(() =>
    data.filter(item =>
      !searchTerm ||
      String(item.Estimateno || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description1 || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.tbl1 || '').toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [data, searchTerm]
  );

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // Running balance computed over full filteredData
  const balances = useMemo(() => {
    let bal = 0;
    return filteredData.map(item => {
      const desc = item.description1 || '';
      if (desc === 'Total' || desc === 'Closing Balance') return null;
      bal += Math.abs(parseFloat(item.debit || 0)) - Math.abs(parseFloat(item.credit || 0));
      return bal;
    });
  }, [filteredData]);

  const handleExportExcel = () => {
    const wb = XLSX.utils.book_new();
    const nowTs = new Date();
    const generatedTime = nowTs.toLocaleString('en-IN', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    });
    const typeLabel = 'Cash';
    const COLS = 6;

    const aoa = [
      [companyName || 'Cash Book Report', ...Array(COLS - 1).fill('')],
      [`Cash Book - ${typeLabel}`, ...Array(COLS - 1).fill('')],
      [`Date Range : ${fromDate}  To : ${toDate}`, ...Array(COLS - 1).fill('')],
      [`Generated : ${generatedTime}`, ...Array(COLS - 1).fill('')],
      Array(COLS).fill(''),
      ['Date', 'Ref No', 'Description', 'Debit', 'Credit', 'Balance'],
      ...filteredData.map((item, i) => {
        const desc = item.description1 || '';
        const debitVal = Math.abs(parseFloat(item.debit || 0));
        const creditVal = Math.abs(parseFloat(item.credit || 0));
        const bal = balances[i];
        const balDisplay = bal === null ? '' : `${fmt(Math.abs(bal))} ${bal < 0 ? 'Cr' : 'Dr'}`;
        return [
          item.date1 || '',
          item.Estimateno || '',
          desc || item.tbl1 || '',
          debitVal > 0 ? debitVal : '',
          creditVal > 0 ? creditVal : '',
          balDisplay,
        ];
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 13 }, { wch: 12 }, { wch: 35 }, { wch: 14 }, { wch: 14 }, { wch: 16 }];
    ws['!rows'] = [{ hpt: 22 }, { hpt: 18 }, { hpt: 16 }, { hpt: 16 }, { hpt: 6 }, { hpt: 20 }];
    ws['!merges'] = [0, 1, 2, 3, 4].map(r => ({ s: { r, c: 0 }, e: { r, c: COLS - 1 } }));

    const infoS = (sz, bold, color = '5c3d1e') => ({
      font: { bold, sz, color: { rgb: color } },
      alignment: { horizontal: 'center', vertical: 'center' },
      fill: { patternType: 'solid', fgColor: { rgb: 'FFF8EE' } },
    });
    ws['A1'].s = infoS(16, true);
    ws['A2'].s = infoS(13, true, '2d3748');
    ws['A3'].s = infoS(11, false, '444444');
    ws['A4'].s = infoS(11, false, '444444');

    const hdrS = {
      font: { bold: true, sz: 11, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '5c3d1e' } },
      alignment: { horizontal: 'center', vertical: 'center' },
      border: {
        top:    { style: 'medium', color: { rgb: '3b2410' } },
        bottom: { style: 'medium', color: { rgb: '3b2410' } },
        left:   { style: 'thin',   color: { rgb: '3b2410' } },
        right:  { style: 'thin',   color: { rgb: '3b2410' } },
      },
    };
    for (let c = 0; c < COLS; c++) {
      const addr = XLSX.utils.encode_cell({ r: 5, c });
      if (ws[addr]) ws[addr].s = hdrS;
    }

    const SPECIAL_FILL = {
      'Opening Balance': 'E8F4FD',
      'Total':           'FFF8E1',
      'Closing Balance': 'E8F5E9',
    };
    const SPECIAL_FONT = {
      'Opening Balance': '1565c0',
      'Total':           'E65100',
      'Closing Balance': '2e7d32',
    };
    const numericCols = new Set([3, 4, 5]);

    filteredData.forEach((item, i) => {
      const desc = item.description1 || '';
      const isSpecial = !!SPECIAL_FILL[desc];
      const even = i % 2 === 0;
      const bg = isSpecial ? SPECIAL_FILL[desc] : (even ? 'FFFFFF' : 'FDF6EC');
      const fontColor = isSpecial ? SPECIAL_FONT[desc] : '2d3748';
      for (let c = 0; c < COLS; c++) {
        const addr = XLSX.utils.encode_cell({ r: 6 + i, c });
        if (!ws[addr]) ws[addr] = { t: 's', v: '' };
        ws[addr].s = {
          font: { sz: 10, bold: isSpecial, color: { rgb: fontColor } },
          fill: { patternType: 'solid', fgColor: { rgb: bg } },
          alignment: { horizontal: numericCols.has(c) ? 'right' : 'left', vertical: 'center' },
          border: {
            top:    { style: 'thin', color: { rgb: 'E0E0E0' } },
            bottom: { style: 'thin', color: { rgb: 'E0E0E0' } },
            left:   { style: 'thin', color: { rgb: 'E0E0E0' } },
            right:  { style: 'thin', color: { rgb: 'E0E0E0' } },
          },
        };
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, 'CashBook');
    XLSX.writeFile(wb, `CashBook_${typeLabel}_${fromDate}_${toDate}.xlsx`, { bookSST: false, cellStyles: true });
  };

  const headerCell = (align = 'left') => ({
    color: 'white', fontWeight: '600', fontSize: '11px',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    padding: '4px 10px', textAlign: align,
    whiteSpace: 'nowrap', backgroundColor: '#5c3d1e',
  });

  const bodyCell = (align = 'left', bold = false) => ({
    fontSize: '12px', padding: '6px 10px',
    color: '#2d3748', whiteSpace: 'nowrap',
    textAlign: align, fontWeight: bold ? '600' : '400',
  });

  const SPECIAL = {
    'Opening Balance': { bg: '#e8f4fd', color: '#1565c0', border: '1px solid #bbdefb' },
    'Total':           { bg: '#fff8e1', color: '#e65100', border: '1px solid #ffe082' },
    'Closing Balance': { bg: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' },
  };

  const pageStartIndex = (currentPage - 1) * itemsPerPage;

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Cash Book..." />}

        <div style={{
          margin: '0 auto', background: 'white', borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px',
          color: 'black', fontFamily: 'Times New Roman', overflow: 'hidden',
        }}>
          {/* Title */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>📒 Cash Book</span>
            </h4>
          </div>

          {/* Filters Row */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>From Date</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <label style={{ fontSize: '12px', fontWeight: '600', color: '#555', whiteSpace: 'nowrap' }}>To Date</label>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              style={{ border: '1px solid #c9a84c', borderRadius: '6px', padding: '3px 8px', fontSize: '12px', height: '29px', background: '#fdf6ec', color: '#333', outline: 'none', cursor: 'pointer' }} />

            <Button variant="contained" onClick={handleRefresh}
              style={{
                background: 'linear-gradient(135deg, #388e3c 0%, #43a047 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(56,142,60,0.4)',
              }}>
              Refresh
            </Button>

            <Button variant="contained" onClick={handleExportExcel} disabled={filteredData.length === 0}
              style={{
                background: filteredData.length === 0 ? '#ccc' : 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                color: 'white', padding: '4px 16px', borderRadius: '6px',
                fontSize: '12px', fontWeight: '700', border: 'none',
                minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap',
                boxShadow: filteredData.length === 0 ? 'none' : '0 2px 8px rgba(21,101,192,0.4)',
              }}>
              📥 Excel
            </Button>

            <div style={{ flex: 1 }} />

            <TextField size="small" placeholder="Search..." value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '18px' }} /></InputAdornment> }}
              sx={{ minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }} />
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <style>{`
              .cashbook-scroll::-webkit-scrollbar { height: 10px; width: 8px; }
              .cashbook-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
              .cashbook-scroll::-webkit-scrollbar-thumb { background: #c9a84c; border-radius: 4px; }
              .cashbook-scroll::-webkit-scrollbar-thumb:hover { background: #5c3d1e; }
              .cashbook-scroll { scrollbar-width: thin; scrollbar-color: #c9a84c #f1f1f1; }
            `}</style>
            <div className="cashbook-scroll" style={{ overflowX: 'scroll', overflowY: 'auto', maxHeight: 'calc(100vh - 260px)', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 0, overflow: 'visible' }}>
                <Table stickyHeader sx={{ minWidth: 700 }} aria-label="cashbook table">
                  <TableHead>
                    <TableRow style={{ height: '27px' }}>
                      <TableCell style={headerCell()}>Date</TableCell>
                      <TableCell style={headerCell()}>Ref No</TableCell>
                      <TableCell style={headerCell()}>Description</TableCell>
                      <TableCell style={headerCell('right')}>Debit</TableCell>
                      <TableCell style={headerCell('right')}>Credit</TableCell>
                      <TableCell style={headerCell('right')}>Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#999', fontSize: '13px' }}>
                          {loading ? 'Loading...' : 'No records found. Select a type and click Refresh.'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedData.map((item, index) => {
                        const globalIndex = pageStartIndex + index;
                        const desc = item.description1 || '';
                        const special = SPECIAL[desc];
                        const displayBalance = balances[globalIndex];
                        const rowBg = special ? special.bg : (index % 2 === 0 ? '#ffffff' : '#fdf6ec');
                        const textColor = special ? special.color : '#2d3748';
                        const debitVal = Math.abs(parseFloat(item.debit || 0));
                        const creditVal = Math.abs(parseFloat(item.credit || 0));
                        return (
                          <TableRow key={item.id || globalIndex} style={{ background: rowBg, borderBottom: special ? special.border : '1px solid #f0f0f0' }}>
                            <TableCell style={{ ...bodyCell(), color: textColor, fontWeight: special ? '700' : '400' }}>{item.date1 || '—'}</TableCell>
                            <TableCell style={{ ...bodyCell('left', true), color: textColor }}>{item.Estimateno || '—'}</TableCell>
                            <TableCell style={{ ...bodyCell(), color: textColor, fontWeight: special ? '700' : '400' }}>{desc || item.tbl1 || '—'}</TableCell>
                            <TableCell style={{ ...bodyCell('right'), color: textColor, fontWeight: special ? '700' : '400' }}>
                              {debitVal > 0 ? fmt(debitVal) : '—'}
                            </TableCell>
                            <TableCell style={{ ...bodyCell('right'), color: textColor, fontWeight: special ? '700' : '400' }}>
                              {creditVal > 0 ? fmt(creditVal) : '—'}
                            </TableCell>
                            <TableCell style={{ ...bodyCell('right', true), color: special ? special.color : (displayBalance < 0 ? '#c62828' : '#2e7d32') }}>
                              {displayBalance === null ? '—' : `${fmt(Math.abs(displayBalance))} ${displayBalance < 0 ? 'Cr' : 'Dr'}`}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>

          <Pagination
            currentPage={currentPage} totalPages={totalPages}
            totalItems={totalItems} itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={n => { setItemsPerPage(n); setCurrentPage(1); }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
      </div>
    </Fade>
  );
}

export default CashBookReport;
